import { useState, useCallback, useRef, useEffect } from 'react'
import {
  parseTelegramHTML,
  parseJournalHTML,
  parseResume,
  parseThreadsExport,
  readFileAsText,
} from '../utils/parsers'

// ---------- Дизайн-токены ----------
const C = {
  bg:         '#05050d',
  surface:    'rgba(255,255,255,0.028)',
  surface2:   'rgba(255,255,255,0.04)',
  border:     'rgba(180,185,230,0.09)',
  borderHi:   'rgba(180,185,230,0.2)',
  accent:     '#b8bdd4',
  accentDim:  'rgba(180,185,230,0.12)',
  accentGlow: 'rgba(180,185,230,0.2)',
  text:       '#dce0f0',
  textMuted:  '#8890aa',
  textDim:    '#6a6e85',
  green:      '#4dc88a',
  red:        '#d45a5a',
  silver:     '#b8bdd4',
  silverHi:   '#dce0f0',
  silverLo:   '#8890aa',
}

const font = {
  heading: "'Cormorant Garamond', serif",
  mono:    "'Space Mono', monospace",
  body:    "'Inter', sans-serif",
}

// ---------- Вспомогательные компоненты ----------

function DropZone({ label, icon, accept, multiple, onFiles, status, children }) {
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef(null)

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault()
      setDragging(false)
      const files = Array.from(e.dataTransfer.files)
      if (files.length) onFiles(files)
    },
    [onFiles]
  )

  const handleChange = (e) => {
    const files = Array.from(e.target.files)
    if (files.length) onFiles(files)
    e.target.value = ''
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        style={{
          border: `1.5px dashed ${dragging ? C.borderHi : status ? C.border : C.borderHi}`,
          borderRadius: 10,
          padding: '20px 16px',
          cursor: 'pointer',
          background: dragging ? C.accentGlow : C.accentDim,
          transition: 'all 0.2s',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 28, marginBottom: 6 }}>{icon}</div>
        <div style={{ fontFamily: font.body, fontSize: 13, color: C.textMuted }}>
          {label}
        </div>
        <div style={{ fontFamily: font.body, fontSize: 11, color: C.textDim, marginTop: 4 }}>
          {accept}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      {status && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 12px',
            background: C.surface,
            borderRadius: 6,
            fontFamily: font.body,
            fontSize: 12,
            color: C.accent,
            border: `1px solid ${C.border}`,
          }}
        >
          {status}
        </div>
      )}
      {children}
    </div>
  )
}

function BlockCard({ title, icon, children }) {
  return (
    <div
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 20,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          fontFamily: font.mono,
          fontSize: 9,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: C.silverLo,
          marginBottom: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span>{icon}</span>
        <span>{title}</span>
      </div>
      {children}
    </div>
  )
}

// ---------- Главный компонент ----------

export default function FileUpload({ onReady, onTelegramChange, onThreadsChange }) {
  const participantMode = false
  const [tgData, setTgData] = useState(null)         // { messages, participants, personalChats, groupChats }
  const [tgLoading, setTgLoading] = useState(false)
  const [myName, setMyName] = useState('')
  const [selfGivenName, setSelfGivenName] = useState('') // имя для __self__ участника
  // Мёрж: mergedGroups = [{ displayName, aliases: [name1, name2, ...] }]
  const [mergedGroups, setMergedGroups] = useState([])
  const [mergeSelected, setMergeSelected] = useState([]) // имена выбранные для мёрджа
  const [mergeName, setMergeName] = useState('')
  const [showMerge, setShowMerge] = useState(false)

  const [journalEntries, setJournalEntries] = useState(null)
  const [journalLoading, setJournalLoading] = useState(false)

  const [resumeText, setResumeText] = useState(null)
  const [resumeLoading, setResumeLoading] = useState(false)
  const [resumeName, setResumeName] = useState('')

  const [threadsData, setThreadsData] = useState(null)
  const [threadsLoading, setThreadsLoading] = useState(false)
  const [threadsInclude, setThreadsInclude] = useState({ posts: true, replies: true, dm: true })
  const [threadsMyName, setThreadsMyName] = useState(null)

  // Сразу сообщаем App о telegram-данных при изменении (до запуска анализа)
  useEffect(() => {
    if (tgData && onTelegramChange) {
      onTelegramChange({ ...tgData, myName: myName.trim(), mergedGroups })
    }
  }, [tgData, myName, mergedGroups, onTelegramChange])

  // Сразу сообщаем App о threads-данных при изменении (до запуска анализа)
  useEffect(() => {
    if (threadsData && onThreadsChange) {
      onThreadsChange({ ...threadsData, threadsMyName })
    }
  }, [threadsData, threadsMyName, onThreadsChange])

  // ---------- Обработчики ----------

  const handleTelegram = useCallback(async (files) => {
    setTgLoading(true)
    try {
      const fileObjs = await Promise.all(files.map(readFileAsText))
      const result = parseTelegramHTML(fileObjs)
      setTgData(result)
      setMyName('')
    } catch (e) {
      console.error('[FileUpload] Telegram parse error:', e)
    } finally {
      setTgLoading(false)
    }
  }, [])

  const handleJournal = useCallback(async (files) => {
    setJournalLoading(true)
    try {
      const fileObjs = await Promise.all(files.map(readFileAsText))
      const entries = parseJournalHTML(fileObjs)
      setJournalEntries(entries)
    } catch (e) {
      console.error('[FileUpload] Journal parse error:', e)
    } finally {
      setJournalLoading(false)
    }
  }, [])

  const handleResume = useCallback(async (files) => {
    if (!files[0]) return
    setResumeLoading(true)
    try {
      const text = await parseResume(files[0])
      setResumeText(text)
      setResumeName(files[0].name)
    } catch (e) {
      console.error('[FileUpload] Resume parse error:', e)
    } finally {
      setResumeLoading(false)
    }
  }, [])

  const handleThreads = useCallback(async (files) => {
    setThreadsLoading(true)
    try {
      const fileObjs = await Promise.all(
        files.map(async (f) => {
          const text = await f.text()
          return { name: f.name, content: text }
        })
      )
      const result = parseThreadsExport(fileObjs)
      setThreadsData(result)
    } catch (e) {
      console.error('[FileUpload] Threads parse error:', e)
    } finally {
      setThreadsLoading(false)
    }
  }, [])

  // ---------- Мёрж участников ----------

  const handleMergeToggle = (name) => {
    setMergeSelected(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    )
  }

  const handleMergeConfirm = () => {
    if (mergeSelected.length < 2 || !mergeName.trim()) return
    setMergedGroups(prev => {
      // убираем выбранные из существующих групп
      const filtered = prev.map(g => ({
        ...g,
        aliases: g.aliases.filter(a => !mergeSelected.includes(a))
      })).filter(g => g.aliases.length > 0)
      return [...filtered, { displayName: mergeName.trim(), aliases: mergeSelected }]
    })
    setMergeSelected([])
    setMergeName('')
  }

  const handleMergeRemove = (displayName) => {
    setMergedGroups(prev => prev.filter(g => g.displayName !== displayName))
  }

  // Имена которые уже вмёрджены
  const mergedAliases = new Set(mergedGroups.flatMap(g => g.aliases))

  // ---------- Готовность к анализу ----------

  const hasTelegram = !!tgData
  const telegramReady = participantMode || !hasTelegram || (hasTelegram && myName.trim())
  const threadsHasSelection = threadsData && (threadsInclude.posts || threadsInclude.replies || threadsInclude.dm)
  const hasAnySource =
    hasTelegram || !!journalEntries || !!resumeText || threadsHasSelection
  const canStart = hasAnySource && telegramReady

  const handleStart = () => {
    if (!canStart) return
    const givenName = selfGivenName.trim()
    let telegramPayload = null
    if (tgData) {
      const messages = givenName
        ? tgData.messages.map(m => m.from === '__self__' ? { ...m, from: givenName } : m)
        : tgData.messages
      const participants = givenName
        ? tgData.participants.map(p => p.name === '__self__' ? { ...p, name: givenName } : p)
        : tgData.participants
      telegramPayload = { ...tgData, messages, participants, myName: myName.trim() === '__self__' ? givenName || '__self__' : myName.trim(), mergedGroups }
    }
    onReady({
      telegram: telegramPayload,
      journal: journalEntries,
      resume: resumeText,
      threads: threadsData ? {
        posts:          threadsInclude.posts   ? threadsData.posts   : [],
        replies:        threadsInclude.replies ? threadsData.replies : [],
        dm:             threadsInclude.dm      ? threadsData.dm      : [],
        dmParticipants: threadsData.dmParticipants || [],
        threadsMyName:  threadsMyName || null,
      } : null,
    })
  }

  // ---------- Статусы ----------

  const tgStatus = tgLoading
    ? 'Парсинг...'
    : tgData
    ? `Личных: ${tgData.personalChats} | Групповых: ${tgData.groupChats} | Всего сообщений: ${tgData.messages.length}`
    : null

  const journalStatus = journalLoading
    ? 'Парсинг...'
    : journalEntries
    ? (() => {
        const dates = journalEntries.map((e) => e.date).filter(Boolean).sort()
        const range =
          dates.length > 0
            ? `${dates[0]} — ${dates[dates.length - 1]}`
            : 'период неизвестен'
        return `Записей: ${journalEntries.length} | ${range}`
      })()
    : null

  const resumeStatus = resumeLoading
    ? 'Загрузка...'
    : resumeText
    ? `${resumeName} — ${resumeText.length} символов`
    : null

  const threadsStatus = threadsLoading
    ? 'Парсинг...'
    : threadsData
    ? `Постов: ${threadsData.posts.length} | Ответов: ${threadsData.replies.length} | DM: ${threadsData.dm.length}`
    : null

  return (
    <div style={{ fontFamily: font.body }}>

      {/* Telegram — скрыт в режиме участника */}
      {!participantMode && <BlockCard title="Telegram" icon="✈">
        <DropZone
          label="Перетащите HTML файлы экспорта Telegram"
          icon="📁"
          accept=".html,.htm"
          multiple
          onFiles={handleTelegram}
          status={tgStatus}
        />

        {tgData && tgData.participants.length > 0 && (
          <div style={{ marginTop: 12 }}>

            {/* Это я */}
            <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, marginBottom: 8 }}>
              Выбери себя:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {tgData.participants.filter(p => p.name !== '__self__').map((p) => {
                const selected = myName === p.name
                return (
                  <button
                    key={p.name}
                    onClick={() => setMyName(p.name)}
                    style={{
                      textAlign: 'left', padding: '5px 10px', borderRadius: 6,
                      border: `1px solid ${selected ? C.accent : C.border}`,
                      background: selected ? C.accentDim : 'transparent',
                      color: selected ? C.accent : C.text,
                      cursor: 'pointer', fontFamily: font.body, fontSize: 12,
                    }}
                  >
                    <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <span>{selected ? 'Вы' : p.name}</span>
                      <span style={{ color: C.textMuted, fontSize: 11 }}>
                        {p.count} сообщ. · {p.chats?.slice(0,2).join(', ')}{p.chats?.length > 2 ? ` +${p.chats.length - 2}` : ''}
                      </span>
                    </span>
                  </button>
                )
              })}
            </div>
            {hasTelegram && !myName && (
              <div style={{ marginTop: 6, fontSize: 12, color: C.accent, fontFamily: font.body }}>
                ⚠ Выбери себя чтобы продолжить
              </div>
            )}

            {/* Собеседник без имени */}
            {tgData.participants.some(p => p.name === '__self__') && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, marginBottom: 6 }}>
                  Собеседник без подписи в экспорте — введи имя:
                </div>
                <input
                  value={selfGivenName}
                  onChange={e => setSelfGivenName(e.target.value)}
                  placeholder="Имя собеседника..."
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '7px 10px', background: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${selfGivenName.trim() ? C.accent : C.border}`,
                    borderRadius: 6, color: C.text,
                    fontFamily: font.body, fontSize: 13, outline: 'none',
                  }}
                />
              </div>
            )}

            {/* Мёрж участников */}
            <div style={{ marginTop: 16, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontFamily: font.mono, fontSize: 11, color: C.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Объединить ники одного человека
                </span>
                <button
                  onClick={() => setShowMerge(v => !v)}
                  style={{ fontSize: 11, fontFamily: font.mono, color: C.accent, background: 'none', border: `1px solid ${C.border}`, borderRadius: 4, padding: '3px 8px', cursor: 'pointer' }}
                >
                  {showMerge ? 'скрыть' : 'развернуть'}
                </button>
              </div>

              {/* Существующие группы */}
              {mergedGroups.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
                  {mergedGroups.map(g => (
                    <div key={g.displayName} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', background: 'rgba(184,189,212,0.06)', borderRadius: 6, border: `1px solid ${C.border}` }}>
                      <span style={{ fontSize: 13, color: C.text, flex: 1 }}>{g.displayName}</span>
                      <span style={{ fontSize: 11, color: C.textMuted }}>← {g.aliases.join(', ')}</span>
                      <button onClick={() => handleMergeRemove(g.displayName)}
                        style={{ fontSize: 11, color: C.red, background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}>✕</button>
                    </div>
                  ))}
                </div>
              )}

              {showMerge && (
                <div>
                  <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 8, fontFamily: font.body }}>
                    Отметь галочкой ники одного человека → введи имя → объедини
                  </div>
                  <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 10 }}>
                    {tgData.participants
                      .filter(p => p.name !== myName && !mergedAliases.has(p.name))
                      .map(p => (
                        <label key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', borderRadius: 5, cursor: 'pointer',
                          background: mergeSelected.includes(p.name) ? 'rgba(184,189,212,0.08)' : 'transparent',
                          border: `1px solid ${mergeSelected.includes(p.name) ? C.accent : C.border}` }}>
                          <input type="checkbox" checked={mergeSelected.includes(p.name)} onChange={() => handleMergeToggle(p.name)}
                            style={{ accentColor: C.accent, width: 13, height: 13 }} />
                          <span style={{ fontSize: 12, color: C.text, flex: 1 }}>{p.name}</span>
                          <span style={{ fontSize: 10, color: C.textMuted }}>
                            {p.count} · {p.chatTypes?.includes('group') ? 'группа' : ''}{p.chatTypes?.includes('personal') ? ' личка' : ''}
                            {p.chats?.length > 0 && <span style={{ marginLeft: 4 }}>· {p.chats.slice(0,2).join(', ')}</span>}
                          </span>
                        </label>
                      ))
                    }
                  </div>
                  {mergeSelected.length >= 2 && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        value={mergeName}
                        onChange={e => setMergeName(e.target.value)}
                        placeholder="Имя человека..."
                        style={{ flex: 1, padding: '6px 10px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontFamily: font.body, fontSize: 13, outline: 'none' }}
                      />
                      <button
                        onClick={handleMergeConfirm}
                        disabled={!mergeName.trim()}
                        style={{ padding: '6px 14px', background: mergeName.trim() ? C.accent : 'transparent', color: mergeName.trim() ? C.bg : C.textMuted,
                          border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: font.mono, fontSize: 12, cursor: mergeName.trim() ? 'pointer' : 'default' }}
                      >
                        объединить
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>
        )}
      </BlockCard>}

      {/* Apple Journal */}
      <BlockCard title="Apple Journal" icon="📔">
        <DropZone
          label="Перетащите HTML файлы дневника"
          icon="🗓"
          accept=".html,.htm"
          multiple
          onFiles={handleJournal}
          status={journalStatus}
        />
      </BlockCard>

      {/* Резюме */}
      <BlockCard title="Резюме" icon="📄">
        <DropZone
          label="Перетащите файл резюме"
          icon="💼"
          accept=".docx,.txt,.md"
          multiple={false}
          onFiles={handleResume}
          status={resumeStatus}
        />
        {resumeText && (
          <div
            style={{
              marginTop: 8,
              padding: '8px 12px',
              background: C.bg,
              borderRadius: 6,
              fontFamily: font.body,
              fontSize: 12,
              color: C.textMuted,
              border: `1px solid ${C.border}`,
              maxHeight: 80,
              overflow: 'hidden',
            }}
          >
            {resumeText.slice(0, 200)}
            {resumeText.length > 200 ? '...' : ''}
          </div>
        )}
      </BlockCard>

      {/* Threads */}
      <BlockCard title="Threads (Meta Export)" icon="🧵">
        <DropZone
          label="Перетащите JSON файлы из Meta Export"
          icon="📦"
          accept=".json"
          multiple
          onFiles={handleThreads}
          status={threadsStatus}
        />

        {threadsData && (
          <div>
            <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, marginBottom: 8 }}>
              Выбери что включить в анализ:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                { key: 'posts',   label: 'Threads посты',    count: threadsData.posts.length },
                { key: 'replies', label: 'Комментарии',      count: threadsData.replies.length },
                { key: 'dm',      label: 'Личные сообщения', count: threadsData.dm.length },
              ].map(({ key, label, count }) => {
                const active = threadsInclude[key]
                return (
                  <button
                    key={key}
                    onClick={() => setThreadsInclude(prev => ({ ...prev, [key]: !prev[key] }))}
                    style={{
                      textAlign: 'left',
                      padding: '7px 12px',
                      borderRadius: 6,
                      border: `1px solid ${active ? C.accent : C.border}`,
                      background: active ? C.accentDim : 'transparent',
                      color: active ? C.accent : C.text,
                      cursor: 'pointer',
                      fontFamily: font.body,
                      fontSize: 13,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                        border: `1px solid ${active ? C.accent : C.textMuted}`,
                        background: active ? C.accent : 'transparent',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 9, color: C.bg,
                      }}>
                        {active ? '✓' : ''}
                      </span>
                      {label}
                    </span>
                    <span style={{ color: C.textMuted, fontSize: 12 }}>{count} шт.</span>
                  </button>
                )
              })}
            </div>
            {!threadsHasSelection && (
              <div style={{ marginTop: 8, fontSize: 12, color: C.accent, fontFamily: font.body }}>
                ⚠ Выбери хотя бы один тип чтобы продолжить
              </div>
            )}

            {/* Выбор себя в DM — как в Telegram */}
            {threadsInclude.dm && threadsData.dmParticipants?.length > 0 && (
              <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
                <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, marginBottom: 8 }}>
                  Выбери себя в DM (твои сообщения пойдут в профиль):
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {threadsData.dmParticipants.map(p => (
                    <button
                      key={p.name}
                      onClick={() => setThreadsMyName(prev => prev === p.name ? null : p.name)}
                      style={{
                        textAlign: 'left', padding: '5px 10px', borderRadius: 6,
                        border: `1px solid ${threadsMyName === p.name ? C.accent : C.border}`,
                        background: threadsMyName === p.name ? C.accentDim : 'transparent',
                        color: threadsMyName === p.name ? C.accent : C.text,
                        cursor: 'pointer', fontFamily: font.body, fontSize: 12,
                      }}
                    >
                      <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <span>{p.name}</span>
                        <span style={{ color: C.textMuted, fontSize: 11 }}>{p.count} сообщ.</span>
                      </span>
                    </button>
                  ))}
                </div>
                {!threadsMyName && (
                  <div style={{ marginTop: 6, fontSize: 11, color: C.textMuted, fontFamily: font.body }}>
                    Не выбрано — DM не будут включены в твой профиль
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </BlockCard>

      {/* Кнопка запуска */}
      <button
        onClick={handleStart}
        disabled={!canStart}
        style={{
          width: '100%',
          padding: '14px 0',
          background: canStart ? C.accentDim : 'transparent',
          color: canStart ? C.silverHi : C.textMuted,
          border: `1px solid ${canStart ? C.borderHi : C.border}`,
          borderRadius: 8,
          fontFamily: font.body,
          fontSize: 13,
          fontWeight: 400,
          cursor: canStart ? 'pointer' : 'not-allowed',
          letterSpacing: '0.08em',
          transition: 'background 0.2s',
          marginTop: 8,
        }}
      >
        {canStart ? 'Начать анализ' : 'Загрузите хотя бы один источник'}
      </button>
    </div>
  )
}
